import { Injectable, signal } from '@angular/core';

import type { EsnToastCloseReason, EsnToastHandle, EsnToastItem, EsnToastOptions } from './types/molecules';

/**
 * App-wide toast queue: any page can confirm an outcome (optionally with an undo) without
 * owning the markup. Unstyled on purpose: the toast outlet renders it.
 */
@Injectable({ providedIn: 'root' })
export class EsnToastService {
  static readonly DEFAULT_DURATION_MS = 4000;

  private nextId = 1;
  private readonly resolvers = new Map<number, (reason: EsnToastCloseReason) => void>();
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly queue = signal<readonly EsnToastItem[]>([]);

  /**
   * The toasts currently on screen, oldest first.
   */
  readonly toasts = this.queue.asReadonly();

  /**
   * Shows a toast and returns a handle that resolves once it has left the screen.
   */
  show(options: EsnToastOptions): EsnToastHandle {
    const item: EsnToastItem = {
      id: this.nextId++,
      message: options.message,
      actionLabel: options.actionLabel ?? null,
      durationMs: options.durationMs ?? EsnToastService.DEFAULT_DURATION_MS,
      tone: options.tone ?? 'neutral',
    };

    const closed = new Promise<EsnToastCloseReason>((resolve) => this.resolvers.set(item.id, resolve));
    this.queue.update((list) => [...list, item]);

    if (item.durationMs > 0) {
      this.timers.set(item.id, setTimeout(() => this.close(item.id, 'timeout'), item.durationMs));
    }

    return { id: item.id, closed, dismiss: () => this.close(item.id, 'dismiss') };
  }

  /**
   * Confirms something the user just did.
   */
  success(message: string): EsnToastHandle {
    return this.show({ message });
  }

  /**
   * Reports a failure; it stays twice as long so it can be read.
   */
  error(message: string): EsnToastHandle {
    return this.show({ message, tone: 'danger', durationMs: EsnToastService.DEFAULT_DURATION_MS * 2 });
  }

  /**
   * The user closed the toast.
   */
  dismiss(id: number): void {
    this.close(id, 'dismiss');
  }

  /**
   * The user chose the toast's action (e.g. Undo); it closes with reason `action`.
   */
  runAction(id: number): void {
    this.close(id, 'action');
  }

  private close(id: number, reason: EsnToastCloseReason): void {
    const resolve = this.resolvers.get(id);
    if (!resolve) return;

    clearTimeout(this.timers.get(id));
    this.timers.delete(id);
    this.resolvers.delete(id);
    this.queue.update((list) => list.filter((t) => t.id !== id));
    resolve(reason);
  }
}
